// Frontend mirror of the backend permission catalog
// (backend/turnus_ai/constants/permissions.py). One-to-one with every key
// the backend serves in /team/permissions/catalog — never a subset. Adding
// a key here without a matching backend key means the button will "gate"
// against a permission no one can ever have; the reverse means the FE is
// blind to it and a member sees a button that always 403s.
//
// Use these via useUser().hasPermission / <PermissionGate> / <GuardedButton>
// so keys are typo-safe. Admins are a wildcard and always pass.

export const PERMISSIONS = {
  // Knowledgebase
  KB_VIEW: "kb.view",
  KB_UPLOAD: "kb.upload",
  KB_DELETE: "kb.delete",

  // Questionnaires (chat-side)
  QUEST_VIEW: "quest.view",
  QUEST_CREATE: "quest.create",
  QUEST_RUN: "quest.run",
  QUEST_DELETE: "quest.delete",

  // Chat
  CHAT_USE: "chat.use",

  // Integrations (SharePoint et al.)
  INTEGRATIONS_VIEW: "integrations.view",
  INTEGRATIONS_MANAGE: "integrations.manage",

  // MC — Supplier inquiries (Block C — writes)
  MC_INQUIRIES_VIEW: "mc.inquiries.view",
  MC_INQUIRIES_SEND: "mc.inquiries.send",
  MC_INQUIRIES_REMIND: "mc.inquiries.remind",

  // MC — Supplier declarations
  MC_DECLARATIONS_VIEW: "mc.declarations.view",
  MC_DECLARATIONS_UPLOAD: "mc.declarations.upload",
  MC_DECLARATIONS_REVIEW: "mc.declarations.review",
  MC_DECLARATIONS_CLASSIFY: "mc.declarations.classify",
  MC_DECLARATIONS_RETRY: "mc.declarations.retry",

  // MC — Customer questionnaires (Customer Studio)
  MC_QUESTIONNAIRES_VIEW: "mc.questionnaires.view",
  MC_QUESTIONNAIRES_PROCESS: "mc.questionnaires.process",
  MC_QUESTIONNAIRES_RECONCILE: "mc.questionnaires.reconcile",

  // MC — Customer declarations (Block E — release)
  MC_CUSTOMER_DECLARATIONS_VIEW: "mc.customer_declarations.view",
  MC_CUSTOMER_DECLARATIONS_GENERATE: "mc.customer_declarations.generate",
  MC_CUSTOMER_DECLARATIONS_RELEASE: "mc.customer_declarations.release",
  MC_CUSTOMER_DECLARATIONS_OVERRIDE: "mc.customer_declarations.override",

  // MC — Customers
  MC_CUSTOMERS_VIEW: "mc.customers.view",
  MC_CUSTOMERS_EDIT: "mc.customers.edit",

  // MC — Master data
  MC_MASTER_DATA_VIEW: "mc.master_data.view",
  MC_MASTER_DATA_EDIT: "mc.master_data.edit",

  // MC — Imports
  MC_IMPORTS_VIEW: "mc.imports.view",
  MC_IMPORTS_UPLOAD: "mc.imports.upload",
  MC_IMPORTS_COMMIT: "mc.imports.commit",
  MC_IMPORTS_DELETE: "mc.imports.delete",
} as const

export type PermissionKey = (typeof PERMISSIONS)[keyof typeof PERMISSIONS]

/**
 * The subset of catalog keys that gate WRITE actions on the backend. A member
 * with none of these has effectively read-only access — surface a banner so
 * they know before they click something that will 403.
 *
 * Keep in sync when adding a new write key to the catalog. Read-only keys
 * (kb.view, quest.view, *.view, chat.use) are intentionally excluded.
 */
/**
 * The five MC studio VIEW keys. A member with at least one of these can see
 * the "Material Compliance" sidebar group; sub-tabs then filter independently.
 * Kept next to the catalog so both sidebars (legacy + V2) import the same list
 * — a rename in the catalog would otherwise leave hardcoded string literals
 * silently mismatched.
 */
export const MC_VIEW_PERMISSIONS: readonly PermissionKey[] = [
  PERMISSIONS.MC_MASTER_DATA_VIEW,
  PERMISSIONS.MC_QUESTIONNAIRES_VIEW,
  PERMISSIONS.MC_DECLARATIONS_VIEW,
  PERMISSIONS.MC_CUSTOMER_DECLARATIONS_VIEW,
  PERMISSIONS.MC_INQUIRIES_VIEW,
]

export const WRITE_PERMISSIONS: readonly PermissionKey[] = [
  PERMISSIONS.KB_UPLOAD,
  PERMISSIONS.KB_DELETE,
  PERMISSIONS.QUEST_CREATE,
  PERMISSIONS.QUEST_RUN,
  PERMISSIONS.QUEST_DELETE,
  PERMISSIONS.INTEGRATIONS_MANAGE,
  PERMISSIONS.MC_INQUIRIES_SEND,
  PERMISSIONS.MC_INQUIRIES_REMIND,
  PERMISSIONS.MC_DECLARATIONS_UPLOAD,
  PERMISSIONS.MC_DECLARATIONS_REVIEW,
  PERMISSIONS.MC_DECLARATIONS_CLASSIFY,
  PERMISSIONS.MC_DECLARATIONS_RETRY,
  PERMISSIONS.MC_QUESTIONNAIRES_PROCESS,
  PERMISSIONS.MC_QUESTIONNAIRES_RECONCILE,
  PERMISSIONS.MC_CUSTOMER_DECLARATIONS_GENERATE,
  PERMISSIONS.MC_CUSTOMER_DECLARATIONS_RELEASE,
  PERMISSIONS.MC_CUSTOMER_DECLARATIONS_OVERRIDE,
  PERMISSIONS.MC_CUSTOMERS_EDIT,
  PERMISSIONS.MC_MASTER_DATA_EDIT,
  PERMISSIONS.MC_IMPORTS_UPLOAD,
  PERMISSIONS.MC_IMPORTS_COMMIT,
  PERMISSIONS.MC_IMPORTS_DELETE,
]
